import { ChevronRight, Home } from 'lucide-react';
import * as React from 'react';
import { Link, useLocation } from 'react-router';
import { cn } from '@/lib/utils';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  isCurrent?: boolean;
}

export interface BreadcrumbProps {
  items: BreadcrumbItem[];
  className?: string;
  showHomeIcon?: boolean;
  maxItems?: number;
}

const Breadcrumb = React.forwardRef<HTMLElement, BreadcrumbProps>(
  ({ className, items, showHomeIcon = true, maxItems = 5, ...props }, ref) => {
    const location = useLocation();
    const pathname = location.pathname;

    // Limit the number of items shown for mobile responsiveness
    const displayItems = items.slice(-maxItems);
    const hasMoreItems = items.length > maxItems;

    return (
      <nav
        ref={ref}
        aria-label="Breadcrumb"
        className={cn('flex items-center space-x-1 text-sm text-gray-500 py-2', className)}
        {...props}
      >
        {/* Home icon/link */}
        {showHomeIcon && (
          <>
            <Link
              to="/home"
              className={cn(
                'flex items-center hover:text-gray-900 transition-colors',
                pathname === '/home' && 'text-gray-900 font-medium',
              )}
              aria-label="Home"
            >
              <Home className="h-4 w-4" />
            </Link>
            {items.length > 0 && <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />}
          </>
        )}

        {/* Overflow indicator for too many items */}
        {hasMoreItems && (
          <>
            <span className="text-gray-400">...</span>
            <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />
          </>
        )}

        {/* Breadcrumb items */}
        {displayItems.map((item, index) => {
          const isLast = index === displayItems.length - 1;

          return (
            <React.Fragment key={`${item.label}-${item.href || index}`}>
              {(index > 0 || hasMoreItems) && (
                <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />
              )}

              {item.href && !isLast ? (
                <Link
                  to={item.href}
                  className={cn(
                    'hover:text-gray-900 transition-colors truncate max-w-[150px] sm:max-w-none',
                    item.isCurrent && 'text-gray-900 font-medium',
                  )}
                  aria-current={item.isCurrent ? 'page' : undefined}
                >
                  {item.label}
                </Link>
              ) : (
                <span
                  className={cn(
                    'text-gray-900 font-medium truncate max-w-[150px] sm:max-w-none',
                    !isLast && 'text-gray-500',
                  )}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {item.label}
                </span>
              )}
            </React.Fragment>
          );
        })}
      </nav>
    );
  },
);

Breadcrumb.displayName = 'Breadcrumb';

export { Breadcrumb };
